// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import Notify from "@guestsupply/App/Components/Notify";
import ProductActions, { ProductActionsStyle } from "@guestsupply/App/Components/Product/ProductActions";
import { ProductGridContainerStyle } from "@guestsupply/App/Components/Product/ProductGridContainer";
import ProductListContainer from "@guestsupply/App/Components/Product/ProductListContainer";
import QtyField from "@guestsupply/App/Components/Qty/QtyField";
import changeAutoDeliveryQuantityOrdered from "@guestsupply/App/Handlers/AutoDelivery/AutoDeliveryChangeQtyOrdered";
import cancelAutoDelivery from "@guestsupply/App/Handlers/AutoDelivery/CancelAutoDeliver";
import createAutoDelivery from "@guestsupply/App/Handlers/AutoDelivery/CreateAutoDeliveryHandler";
import getEstimateTotals from "@guestsupply/App/Handlers/AutoDelivery/GetEstimateTotalsHandler";
import loadAutoDeliveryOrders from "@guestsupply/App/Handlers/AutoDelivery/loadAutoDeliveryOrders";
import setAutoDeliveryModalClosed from "@guestsupply/App/Handlers/AutoDelivery/SetAutoDeliveryModalClosed";
import updateAutoDelivery from "@guestsupply/App/Handlers/AutoDelivery/UpdateAutoDeliveryHandler";
import updateAutoDeliveryParameter from "@guestsupply/App/Handlers/AutoDelivery/UpdateAutoDeliveryParameterHandler";
import { GetAutoDeliveryCollectionApiParameter } from "@guestsupply/App/Services/AutoDelivery";
import { CustomAppState } from "@guestsupply/App/Store/Reducers";
import { getSettingsCollection } from "@guestsupply/App/Store/Selectors/SettingsSelectors";
import { SelectPresentationProps } from "@guestsupply/MobiusOverrides/Select/Select";
import TextField, { TextFieldPresentationProps } from "@guestsupply/MobiusOverrides/TextField/TextField";
import Tooltip, { TooltipPresentationProps } from "@guestsupply/MobiusOverrides/Tooltip/Tooltip";
import ProductListProductImage, {
    ProductListProductImageStyles,
} from "@guestsupply/Overrides/Widgets/ProductList/ProductListProductImage";
import QuestionTooltip from "@guestsupply/XDL/Icons/QuestionTooltip";
import useAccessibleSubmit from "@insite/client-framework/Common/Hooks/useAccessibleSubmit";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { convertDateToDateOnlyString } from "@insite/client-framework/Common/Utilities/DateUtilities";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import loadCurrentShipTos from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadCurrentShipTos";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import translate from "@insite/client-framework/Translate";
import ProductPrice from "@insite/content-library/Components/ProductPrice";
import ProductListProductInformation from "@insite/content-library/Widgets/ProductList/ProductListProductInformation";
import Accordion, { AccordionPresentationProps } from "@insite/mobius/Accordion";
import { AccordionSectionPresentationProps, ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import Button, { ButtonProps } from "@insite/mobius/Button";
import DatePicker, {
    DatePickerPresentationProps,
    DatePickerState,
    DateTimePickerLibComponentProps,
} from "@insite/mobius/DatePicker";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon from "@insite/mobius/Icon";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Select from "@insite/mobius/Select";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: ApplicationState) => ({
    isAuthenticated: getSession(state).isAuthenticated,
    isUsWebsite: getSettingsCollection(state).customSettings?.isUsWebSite,
    billTo: getCurrentBillToState(state)?.value,
    shipTo: getShipToState(state, getSession(state).shipToId)?.value,
    billToId: getSession(state).billToId,
    shipToId: getSession(state).shipToId,
    isOpen: (state as CustomAppState).xAutoDelivery.modalData?.isOpen,
    autoDeliveryAndCarriersData: (state as CustomAppState).xAutoDelivery.modalData?.autoDeliveryAndCarriersData,
    estimateTotalsData: (state as CustomAppState).xAutoDelivery.modalData?.estimateTotalsData,
    errorMessage: (state as CustomAppState).xAutoDelivery.modalData?.errorMessage,
    isDataLoading: (state as CustomAppState).xAutoDelivery.modalData?.isDataLoading,
    productContext: (state as CustomAppState).xAutoDelivery.modalData?.productContext,
    currentPage: getCurrentPage(state).type,
    autoDeliveryParameter: (state as CustomAppState).xAutoDelivery.parameter,
});

const mapDispatchToProps = {
    loadCurrentShipTos,
    updateAutoDelivery,
    createAutoDelivery,
    cancelAutoDelivery,
    setAutoDeliveryModalClosed,
    changeAutoDeliveryQuantityOrdered,
    updateAutoDeliveryParameter,
    loadAutoDeliveryOrders,
    getEstimateTotals,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface AutoDeliveryModalStyles {
    managedAccordionSectionStyle?: AccordionSectionPresentationProps;
    productListContainerStyles?: ProductGridContainerStyle;
    orderSummaryGridContainer?: GridContainerProps;
    titleGridItem?: GridItemProps;
    titleText?: TypographyProps;
    labelGridItem?: GridItemProps;
    labelText?: TypographyProps;
    valueGridItem?: GridItemProps;
    dividerGridItem?: GridItemProps;
    totalValue?: TypographyProps;
    valueText?: TypographyProps;
    totalLabel?: TypographyProps;
    AdditionalInfoGridItem?: GridItemProps;
    AdditionalInfoText?: TypographyProps;
    accordionWrapperStyle?: InjectableCss;
    accordionStyle?: AccordionPresentationProps;
    shiptoText?: TextFieldPresentationProps;
    productActionsWrapper?: InjectableCss;
    deliveryScheduleWrapper?: InjectableCss;
    creationDateStyle?: DatePickerPresentationProps;
    repeationStyle?: SelectPresentationProps;
    orderSummaryWrapper?: InjectableCss;
    submitButton?: ButtonProps;
    productActionsStyle?: ProductActionsStyle;
    tooltipWrapper?: InjectableCss;
    tooltipContainer?: TooltipPresentationProps;
    notify?: TypographyProps;
    productImage?: ProductListProductImageStyles;
    modal?: ModalPresentationProps;
}

export const autoDeliveryModalStyles: AutoDeliveryModalStyles = {};

const styles = autoDeliveryModalStyles;

const CreationDate = () => {
    return (
        <Tooltip
            text={siteMessage("GUEST_AutoDelivery_Creation_Date", "Creation Date")}
            triggerComponent={
                <StyledWrapper {...styles.tooltipWrapper}>
                    <Typography>{translate("Creation Date")}</Typography>
                    <Icon src={QuestionTooltip} width={13} height={13} />
                </StyledWrapper>
            }
            {...styles.tooltipContainer}
        />
    );
};
const ProductActionsAndPrice = ({ productContext }: { productContext: ProductContextModel }) => {
    const {
        product: { minimumOrderQty, detail },
        onQtyOrderedChanged,
        productInfo: { qtyOrdered },
    } = productContext;

    const [minQtyState, setMinQty] = useState(1);
    const [qty, setQty] = useState(1);

    useEffect(() => {
        const minQty = Math.max(minimumOrderQty, detail?.multipleSaleQty || 1);
        setMinQty(minQty);
    }, [minimumOrderQty, detail?.multipleSaleQty, qtyOrdered]);

    const localOnQtyOrderedChange = (value: string) => {
        let inputQty = parseFloat(value);
        if (inputQty < minQtyState) {
            inputQty = qtyOrdered;
            setQty(inputQty);
        }
        onQtyOrderedChanged?.(inputQty);
    };

    const { value, changeHandler, keyDownHandler, blurHandler } = useAccessibleSubmit(
        qtyOrdered.toString(),
        localOnQtyOrderedChange,
        qty,
    );
    return (
        <StyledWrapper {...styles.productActionsWrapper}>
            <QtyField
                showLabel={false}
                value={value}
                type="number"
                min={minQtyState}
                showMultipleSaleQty={detail?.multipleSaleQty ? detail?.multipleSaleQty > 1 : false}
                max={9999}
                data-test-selector="product_qtyOrdered"
                onChange={changeHandler}
                onKeyDown={keyDownHandler}
                onBlur={blurHandler}
            />
            <ProductPrice showLabel={false} showInvalidPriceMessage={false} product={productContext} />
        </StyledWrapper>
    );
};
const AutoDeliveryModal = ({
    isOpen,
    productContext,
    billTo,
    shipTo,
    loadCurrentShipTos,
    autoDeliveryAndCarriersData,
    updateAutoDelivery,
    billToId,
    shipToId,
    createAutoDelivery,
    cancelAutoDelivery,
    updateAutoDeliveryParameter,
    estimateTotalsData,
    errorMessage,
    isDataLoading,
    currentPage,
    autoDeliveryParameter,
    setAutoDeliveryModalClosed,
    changeAutoDeliveryQuantityOrdered,
    loadAutoDeliveryOrders,
    getEstimateTotals,
}: Props) => {
    const minDate = new Date();
    minDate.setDate(minDate.getDate() + 1);
    const [postalCode, setPostalCode] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [deliveredEvery, setDeliveredEvery] = useState("");
    const [creationDate, setCreationDate] = useState<Date | undefined>(minDate);
    const [shipViaId, setShipViaId] = useState("");
    const [carrierId, setCarrierId] = useState("");
    const [showError, setShowError] = useState(false);

    const { isTabletScreen } = useAppSelector(state => ({
        isTabletScreen: (state as CustomAppState).xCommon.isTabletScreen,
    }));

    const toasterContext = useContext(ToasterContext);

    const autoDeliveryData = autoDeliveryAndCarriersData?.autoDeliveryData;
    const autoCarriersData = autoDeliveryAndCarriersData?.autoCarriersData;

    let shipToLabel = shipTo?.label;
    if (billTo?.id === shipTo?.id) {
        shipToLabel = "Use Billing Address";
    }

    useEffect(() => {
        setCreationDate(minDate);
    }, []);

    useEffect(() => {
        if (autoDeliveryData?.defaultPurchaseOrderNo) {
            setPostalCode(autoDeliveryData?.defaultPurchaseOrderNo);
        }

        if (autoDeliveryData?.customerAutoDeliveryDto?.startDate) {
            setCreationDate(new Date(autoDeliveryData?.customerAutoDeliveryDto.startDate));
        }
        if (autoDeliveryData?.customerAutoDeliveryDto?.repetition) {
            setDeliveredEvery(autoDeliveryData?.customerAutoDeliveryDto?.repetition);
        }
        autoCarriersData?.carrierDtos.forEach(item => {
            if (item.shipVias) {
                item.shipVias.forEach(shipVia => {
                    if (shipVia.isDefault) {
                        setShipViaId(shipVia.id);
                        setCarrierId(item.id);
                    }
                });
            }
        });
    }, [autoDeliveryData?.defaultPurchaseOrderNo, isOpen]);

    if (!productContext) {
        return null;
    }

    productContext = {
        ...productContext,
        onQtyOrderedChanged: (qtyOrdered: number) => {
            changeAutoDeliveryQuantityOrdered({ productId: productContext!.productInfo.productId, qtyOrdered });
            if (qtyOrdered !== estimateTotalsData?.qty) {
                setIsLoading(true);
                getEstimateTotals({
                    productId: productContext!.productInfo.productId,
                    qty: qtyOrdered,
                    shipViaId,
                    uom: productContext!.productInfo.unitOfMeasure,
                    onError: errorMessage => {
                        toasterContext.addToast({
                            body: errorMessage,
                            messageType: "danger",
                        });
                        setAutoDeliveryModalClosed({});
                    },
                    onComplete: () => {
                        setIsLoading(false);
                    },
                });
            }
        },
        productInfo: {
            ...productContext.productInfo,
            qtyOrdered: estimateTotalsData?.qty || 1,
        },
    };
    const handleSubmitClick = () => {
        if (!deliveredEvery.length || !postalCode.length) {
            setShowError(true);
            return;
        }

        const { productId, qtyOrdered, unitOfMeasure } = productContext!.productInfo;

        setIsLoading(true);
        if (autoDeliveryData?.customerAutoDeliveryDto) {
            updateAutoDelivery({
                ...autoDeliveryData?.customerAutoDeliveryDto,
                productId,
                purchaseOrderNo: postalCode,
                qty: qtyOrdered,
                uom: unitOfMeasure,
                startDate: convertDateToDateOnlyString(creationDate),
                repetition: deliveredEvery,
                onComplete: result => {
                    if (!result?.apiResult?.successful) {
                        toasterContext.addToast({
                            body: result?.apiResult?.errorMessage,
                            messageType: "danger",
                        });
                    } else {
                        toasterContext.addToast({
                            body: siteMessage("Custom_AutoDelivery_Update", "Auto Delivery Updated Successfully"),
                            messageType: "success",
                        });
                    }
                    setIsLoading(false);
                    if (autoDeliveryParameter && currentPage === "AutoDeliveryPage") {
                        loadAutoDeliveryOrders(autoDeliveryParameter);
                    }
                    setAutoDeliveryModalClosed({});
                    setDeliveredEvery("");
                    setCreationDate(minDate);
                },
            });
        } else {
            createAutoDelivery({
                productId,
                shipToId: shipToId ?? "",
                carrierId,
                shipViaId,
                purchaseOrderNo: postalCode,
                qty: qtyOrdered,
                uom: unitOfMeasure,
                startDate: convertDateToDateOnlyString(creationDate),
                repetition: deliveredEvery,
                onComplete: result => {
                    if (!result?.apiResult?.successful) {
                        toasterContext.addToast({
                            body: result?.apiResult?.errorMessage,
                            messageType: "danger",
                        });
                    } else {
                        toasterContext.addToast({
                            body: siteMessage("Custom_AutoDelivery_Create", "Auto Delivery Created Successfully"),
                            messageType: "success",
                        });
                    }
                    setAutoDeliveryModalClosed({});
                    setIsLoading(false);
                    setDeliveredEvery("");
                    setCreationDate(minDate);
                },
            });
        }
    };
    const handleCancelClick = () => {
        const { productId } = productContext!.productInfo;
        setIsLoading(true);

        cancelAutoDelivery({
            productId,
            onComplete: result => {
                if (!result?.apiResult?.successful) {
                    toasterContext.addToast({
                        body: result?.apiResult?.errorMessage,
                        messageType: "danger",
                    });
                } else {
                    toasterContext.addToast({
                        body: siteMessage("Custom_AutoDelivery_Cancel", "Auto Delivery Canceled Successfully"),
                        messageType: "success",
                    });
                }
                setIsLoading(false);
                setAutoDeliveryModalClosed({});
                setDeliveredEvery("");
                setCreationDate(minDate);
                // refresh auto-delivery orders after canceling an auto-delivery
                if (currentPage === "AutoDeliveryPage") {
                    updateAutoDeliveryParameter({
                        ...(autoDeliveryParameter as GetAutoDeliveryCollectionApiParameter),
                        cancelOrder: productId,
                    });
                }
            },
        });
    };
    const handleDatePickerDayChange = ({ selectedDay, isEmpty }: DatePickerState) => {
        if (!selectedDay && !isEmpty) {
            setCreationDate(creationDate);
            return;
        }
        setCreationDate(selectedDay);
    };
    const handleModalClose = () => {
        setAutoDeliveryModalClosed({});
        setDeliveredEvery("");
        setCreationDate(minDate);
    };

    const today = new Date();
    const disabledButton =
        !postalCode.length ||
        !deliveredEvery.length ||
        errorMessage !== undefined ||
        isLoading ||
        isDataLoading ||
        (creationDate! < today && !autoDeliveryData?.customerAutoDeliveryDto) ||
        !creationDate;

    return (
        <Modal
            sizeVariant="large"
            headline={translate("Auto-Delivery Preferences")}
            isOpen={isOpen}
            handleClose={handleModalClose}
            {...styles.modal}
        >
            <LoadingOverlay loading={isLoading || isDataLoading}>
                <ProductContext.Provider value={productContext}>
                    <StyledWrapper {...styles.accordionWrapperStyle}>
                        <Accordion headingLevel={4} {...styles.accordionStyle}>
                            {/* Postal Code Accordion */}
                            <ManagedAccordionSection
                                {...styles.managedAccordionSectionStyle}
                                title={translate("Purchase Order #")}
                                initialExpanded={true}
                                onTogglePanel={() => {}}
                            >
                                <TextField
                                    label="PO #"
                                    placeholder={translate("99800")}
                                    value={postalCode}
                                    required
                                    maxLength={25}
                                    onChange={e => setPostalCode(e.target.value)}
                                    data-test-selector="PostalCode"
                                    {...styles.shiptoText}
                                />
                            </ManagedAccordionSection>
                            {/* Product Details Accordion */}
                            <ManagedAccordionSection
                                {...styles.managedAccordionSectionStyle}
                                title={translate("My Product")}
                                initialExpanded={true}
                            >
                                <StyledWrapper>
                                    <StyledWrapper>
                                        <ProductListContainer extendedStyles={styles.productListContainerStyles}>
                                            <ProductListContainer.Image>
                                                <ProductListProductImage
                                                    showImage={true}
                                                    showCompare={false}
                                                    showImageBadge={false}
                                                    showTextBadge={false}
                                                    isGridView={false}
                                                    extendedStyles={styles.productImage}
                                                />
                                            </ProductListContainer.Image>
                                            <ProductListContainer.Info>
                                                <ProductListProductInformation
                                                    showBrand={false}
                                                    showTitle={true}
                                                    showPartNumbers={true}
                                                    showAvailability={false}
                                                    showAttributes={false}
                                                    // XNG-CHANGES -GUEST-2682 : set showImageBadges to true
                                                    showImageBadges={true}
                                                    maxImageBadges={4}
                                                    showTextBadges={false}
                                                    maxTextBadges={4}
                                                    {...{ showShippingWeight: false, showPackDescription: false }}
                                                />
                                                {!isTabletScreen && (
                                                    <ProductActionsAndPrice productContext={productContext} />
                                                )}
                                            </ProductListContainer.Info>
                                        </ProductListContainer>
                                    </StyledWrapper>
                                    {isTabletScreen && <ProductActionsAndPrice productContext={productContext} />}
                                    {estimateTotalsData?.isQtyAdjusted && (
                                        <Notify type="info" {...styles.notify}>
                                            {siteMessage(
                                                "Guest_Product_QuantitiesAdjusted",
                                                "The quantity for products was changed due to rounding or minimum quantity rules.",
                                            )}
                                        </Notify>
                                    )}
                                </StyledWrapper>
                            </ManagedAccordionSection>
                            {/* Delivery Schedule Accordion */}
                            <ManagedAccordionSection
                                {...styles.managedAccordionSectionStyle}
                                title={translate("Delivery Schedule")}
                                initialExpanded={true}
                            >
                                <StyledWrapper {...styles.deliveryScheduleWrapper}>
                                    <DatePicker
                                        onDayChange={handleDatePickerDayChange}
                                        selectedDay={creationDate}
                                        {...styles.creationDateStyle}
                                        dateTimePickerProps={{
                                            minDate,
                                        }}
                                        error=""
                                        label={<>{CreationDate()}</>}
                                    />
                                    <StyledWrapper>
                                        <Select
                                            label={translate("Delivered Every")}
                                            required
                                            placeholder="Select"
                                            error={showError && translate("Delivered Every is a Required field")}
                                            onChange={event => {
                                                setDeliveredEvery(event.target.value);
                                                setShowError(false);
                                            }}
                                            value={deliveredEvery}
                                            {...styles.repeationStyle}
                                        >
                                            <option value="">{translate("Select Option")}</option>
                                            {autoDeliveryData?.repeatsDto.map((item, id) => (
                                                <option key={`${item}_${id}`} value={item}>
                                                    {item}
                                                </option>
                                            ))}
                                        </Select>
                                    </StyledWrapper>
                                </StyledWrapper>
                            </ManagedAccordionSection>
                            {/* Shipping Accordion */}

                            <ManagedAccordionSection
                                initialExpanded={true}
                                {...styles.managedAccordionSectionStyle}
                                title={translate("Shipping")}
                            >
                                <TextField
                                    label="Shipping To"
                                    placeholder={shipToLabel}
                                    disabled
                                    data-test-selector="ShippingTo"
                                    {...styles.shiptoText}
                                />
                            </ManagedAccordionSection>
                        </Accordion>
                        <StyledWrapper className="order-summary" {...styles.orderSummaryWrapper}>
                            <GridContainer {...styles.orderSummaryGridContainer}>
                                <GridItem {...styles.titleGridItem}>
                                    <Typography {...styles.titleText}>{translate("Order Summary")}</Typography>
                                </GridItem>
                                <GridItem {...styles.labelGridItem}>
                                    <Typography {...styles.labelText}>{translate("Subtotal")}</Typography>
                                </GridItem>
                                <GridItem {...styles.valueGridItem}>
                                    <Typography {...styles.valueText}>
                                        {estimateTotalsData?.orderSubTotalDisplay ?? "--"}
                                    </Typography>
                                </GridItem>
                                <GridItem {...styles.labelGridItem}>
                                    <Typography {...styles.labelText}>{translate("Est. Shipping")}</Typography>
                                </GridItem>
                                <GridItem {...styles.valueGridItem}>
                                    <Typography {...styles.valueText}>
                                        {estimateTotalsData?.shippingAndHandlingDisplay ?? "--"}
                                    </Typography>
                                </GridItem>
                                <GridItem {...styles.labelGridItem}>
                                    <Typography {...styles.labelText}>{translate("Est. Tax")}</Typography>
                                </GridItem>
                                <GridItem {...styles.valueGridItem}>
                                    <Typography {...styles.valueText}>
                                        {estimateTotalsData?.totalTaxDisplay ?? "--"}
                                    </Typography>
                                </GridItem>
                                <GridItem {...styles.dividerGridItem} />
                                <GridItem {...styles.labelGridItem}>
                                    <Typography {...styles.totalLabel}>{translate("Est. Total")}</Typography>
                                </GridItem>
                                <GridItem {...styles.valueGridItem}>
                                    <Typography
                                        {...styles.totalValue}
                                        data-test-selector="orderDetail_orderGrandTotalDisplay"
                                    >
                                        {estimateTotalsData?.orderGrandTotalDisplay ?? "--"}
                                    </Typography>
                                </GridItem>
                                <GridItem {...styles.AdditionalInfoGridItem}>
                                    <Typography {...styles.AdditionalInfoText}>
                                        {siteMessage(
                                            "Guest_AutoReorder_CompleteOrderForFinalShippingAndTax",
                                            "Availability, Shipping, Tax & Promotions are not final until you complete your order.",
                                        )}
                                    </Typography>
                                </GridItem>
                                <GridItem {...styles.AdditionalInfoGridItem}>
                                    <Button
                                        disabled={disabledButton}
                                        onClick={handleSubmitClick}
                                        aria-label={`${
                                            autoDeliveryData?.customerAutoDeliveryDto ? "Update" : "Submit"
                                        } Order Preferences`}
                                        {...styles.submitButton}
                                    >
                                        {translate(
                                            `${
                                                autoDeliveryData?.customerAutoDeliveryDto ? "Update" : "Submit"
                                            } Order Preferences`,
                                        )}
                                    </Button>
                                </GridItem>
                                {autoDeliveryData?.customerAutoDeliveryDto && (
                                    <GridItem {...styles.AdditionalInfoGridItem}>
                                        <Button
                                            variant="tertiary"
                                            disabled={isLoading || errorMessage !== undefined}
                                            onClick={handleCancelClick}
                                            aria-label="Cancel Auto-Delivery"
                                            {...styles.submitButton}
                                        >
                                            {translate("Cancel Auto-Delivery")}
                                        </Button>
                                    </GridItem>
                                )}
                            </GridContainer>
                            {errorMessage !== undefined && (
                                <Notify {...styles.notify} type="danger">
                                    {errorMessage}
                                </Notify>
                            )}
                        </StyledWrapper>
                    </StyledWrapper>
                </ProductContext.Provider>
            </LoadingOverlay>
        </Modal>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(AutoDeliveryModal);
